import { useMemo } from 'react';
import { VStack } from '@components/base/layout';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getUserFormConfig } from '../../configs/userFormConfig';
import { usersService } from '../../api/usersService';
import type { StoreUserRequest, UpdateUserRequest } from '../../api/usersService';
import { UserRolesPanel } from './UserRolesPanel';

interface UserParams {
  id?: string;
}

export function UserFormPage() {
  const formPage = useAdminFormPage<unknown, UserParams>({
    entityName: 'user',
    getConfig: (mode, params) => getUserFormConfig(mode, params.id),
    getEditId: (params) => params.id,
    queryKey: (params) => ['admin', 'users', params.id],
    fetchFn: (params) => usersService.get(params.id!),
    mapToInitialValues: (entity) => {
      const user = entity as Record<string, unknown>;
      return {
        name: user.name,
        email: user.email,
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      if (isEdit && params.id) {
        const updatePayload: UpdateUserRequest = {
          name: payload.name as string,
          email: payload.email as string,
        };
        const password = payload.password as string | undefined;
        if (password && password.trim() !== '') {
          updatePayload.password = password;
        }
        return usersService.update(params.id, updatePayload);
      }
      const createPayload: StoreUserRequest = {
        name: payload.name as string,
        email: payload.email as string,
        password: payload.password as string,
      };
      return usersService.create(createPayload);
    },
    invalidateKey: () => ['admin', 'users'],
    navigateBackPath: () => '/admin/users',
  });

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'Users', href: '/admin/users' },
    { label: formPage.isEdit ? 'Edit User' : 'Create User' },
  ], [formPage.isEdit]);

  const errorBreadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'Users', href: '/admin/users' },
    { label: 'Edit' },
  ], []);

  return (
    <VStack gap={6}>
      <AdminFormPage
        formPage={formPage}
        entityLabel="User"
        breadcrumbs={breadcrumbs}
        errorBreadcrumbs={errorBreadcrumbs}
        genericFetchErrorMessage="An unexpected error occurred while loading the user."
      />
      {formPage.isEdit &&
        formPage.params.id &&
        !formPage.isFetchLoading &&
        !formPage.fetchError && (
          <UserRolesPanel userId={formPage.params.id} />
        )}
    </VStack>
  );
}
